import { createFileRoute, Link } from "@tanstack/react-router";
import * as React from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Search, Loader2, UserPlus, X, Lock, Unlock, Bell, CheckCircle2, DollarSign, Save, Download } from "lucide-react";
import { exportToCSV, todayStamp } from "@/lib/export-csv";
import { format } from "date-fns";
import { PortalLayout } from "@/components/portal-layout";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { supabase } from "@/integrations/supabase/client";
import { useAuth, useCanMutate } from "@/lib/auth";
import { UserAvatar } from "@/components/user-avatar";
import { toast } from "sonner";
import { useServerFn } from "@tanstack/react-start";
import { createUserAccount, resetUserPassword, banUser, unbanUser } from "@/lib/admin-users.functions";
import { UpgradeDialog } from "@/components/UpgradeDialog";
import { useEntitlements } from "@/hooks/use-entitlements";

export const Route = createFileRoute("/admin/students")({ component: UsersPage });

type UserRow = {
  id: string; display_name: string | null; email: string;
  avatar_key: string | null; avatar_url: string | null; is_banned: boolean;
  phone: string | null; address: string | null; date_of_birth: string | null;
};
type ClassOpt = { id: string; name: string; teacher_id: string | null };
type Enrollment = { id: string; class_id: string; student_id: string };
type Tab = "student" | "teacher";
type StatusFilter = "all" | "active" | "no_class" | "banned";

let _myCenterIdCache: string | null | undefined;
let _myCenterIdUserId: string | undefined;
async function getMyCenterId(): Promise<string | null> {
  try {
    const { data: ures } = await supabase.auth.getUser();
    const uid = ures.user?.id;
    if (!uid) { _myCenterIdCache = null; _myCenterIdUserId = undefined; return null; }
    if (_myCenterIdUserId !== uid) {
      _myCenterIdCache = undefined;
      _myCenterIdUserId = uid;
    }
    if (_myCenterIdCache !== undefined) return _myCenterIdCache ?? null;
    const { data } = await supabase.from("profiles").select("center_id").eq("id", uid).maybeSingle();
    _myCenterIdCache = (data as { center_id?: string | null } | null)?.center_id ?? null;
    return _myCenterIdCache ?? null;
  } catch { _myCenterIdCache = null; return null; }
}

async function notifyUsers(opts: {
  title: string; body: string;
  audience: "all" | "students" | "teachers";
  classId?: string | null; createdBy?: string | null;
  targetUserId?: string | null;
  centerId?: string | null;
}) {
  try {
    const cid = opts.centerId ?? await getMyCenterId();
    await supabase.from("announcements").insert({
      title: opts.title, body: opts.body, audience: opts.audience,
      class_id: opts.classId ?? null, created_by: opts.createdBy ?? null,
      target_user_id: opts.targetUserId ?? null,
      center_id_owner: cid,
    });
  } catch (e) { console.error("notify error", e); }
}


function curMonth() {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
}
function lastNMonths(n: number): string[] {
  const out: string[] = [];
  const now = new Date();
  for (let i = 0; i < n; i++) {
    const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
    out.push(`${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`);
  }
  return out;
}
function fmtMoney(n: number) {
  return new Intl.NumberFormat("vi-VN").format(Math.round(n || 0));
}

type FeeRow = {
  id: string; student_id: string; month: string;
  total_fee: number; paid_amount: number; is_paid: boolean; paid_at: string | null;
};
type SalaryRow = {
  id: string; teacher_id: string; month: string;
  salary_amount: number; is_paid: boolean; paid_at: string | null;
};

function StudentFeesTab({ student, adminId, centerId }: { student: UserRow; adminId?: string; centerId?: string | null }) {
  const qc = useQueryClient();
  const canMutate = useCanMutate();
  const months = React.useMemo(() => lastNMonths(6), []);
  const cur = curMonth();

  const profileQ = useQuery({
    queryKey: ["fees-profile", student.id],
    queryFn: async () => {
      const { data, error } = await supabase
        .rpc("get_profile_private", { _user_id: student.id });
      if (error) throw error;
      const row = (data ?? [])[0] as { monthly_tuition: number | null } | undefined;
      return { monthly_tuition: row?.monthly_tuition ?? null };
    },
  });

  const feesQ = useQuery({
    queryKey: ["fees", student.id],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("student_fees").select("*")
        .eq("student_id", student.id).in("month", months);
      if (error) throw error;
      return (data ?? []) as FeeRow[];
    },
  });

  // Auto-generate current month fee
  React.useEffect(() => {
    if (!feesQ.data || !profileQ.data) return;
    const hasCur = feesQ.data.some((f) => f.month === cur);
    const tuition = Number(profileQ.data.monthly_tuition ?? 0);
    if (!hasCur && tuition > 0) {
      supabase.from("student_fees").insert({
        student_id: student.id, month: cur,
        total_fee: tuition, paid_amount: 0, is_paid: false, created_by: adminId ?? null,
        center_id: centerId ?? null,
      }).then(() => qc.invalidateQueries({ queryKey: ["fees", student.id] }));
    }
  }, [feesQ.data, profileQ.data, cur, student.id, adminId, centerId, qc]);

  const [tuitionInput, setTuitionInput] = React.useState<string>("");
  React.useEffect(() => {
    if (profileQ.data) setTuitionInput(String(profileQ.data.monthly_tuition ?? 0));
  }, [profileQ.data]);

  const saveTuitionMut = useMutation({
    mutationFn: async () => {
      const v = Number(tuitionInput) || 0;
      const { error } = await supabase.from("profiles")
        .update({ monthly_tuition: v }).eq("id", student.id);
      if (error) throw error;
    },
    onSuccess: () => {
      toast.success("Đã lưu mức học phí mặc định");
      qc.invalidateQueries({ queryKey: ["fees-profile", student.id] });
    },
    onError: (e: any) => toast.error(e?.message ?? "Lỗi"),
  });

  const [editPaid, setEditPaid] = React.useState<Record<string, string>>({});

  const updateFeeMut = useMutation({
    mutationFn: async (vars: { row: FeeRow; paid: number; markPaid?: boolean }) => {
      const patch: any = { paid_amount: vars.paid };
      if (vars.markPaid !== undefined) {
        patch.is_paid = vars.markPaid;
        patch.paid_at = vars.markPaid ? new Date().toISOString() : null;
      } else {
        patch.is_paid = vars.paid >= Number(vars.row.total_fee);
        patch.paid_at = patch.is_paid ? new Date().toISOString() : null;
      }
      const { error } = await supabase.from("student_fees").update(patch).eq("id", vars.row.id);
      if (error) throw error;
      return { ...vars, patch };
    },
    onSuccess: async (res) => {
      toast.success("Đã cập nhật");
      if (res.patch.is_paid) {
        await notifyUsers({
          title: "Đã ghi nhận thanh toán học phí",
          body: `Học phí tháng ${res.row.month} (${fmtMoney(res.row.total_fee)}đ) đã được ghi nhận đã thanh toán.`,
          audience: "students", createdBy: adminId, targetUserId: student.id,
        });

      }
      qc.invalidateQueries({ queryKey: ["fees", student.id] });
      setEditPaid((s) => { const n = { ...s }; delete n[res.row.id]; return n; });
    },
    onError: (e: any) => toast.error(e?.message ?? "Lỗi"),
  });

  const remindMut = useMutation({
    mutationFn: async (row: FeeRow) => {
      const remain = Number(row.total_fee) - Number(row.paid_amount);
      await notifyUsers({
        title: `Nhắc đóng học phí tháng ${row.month}`,
        body: `Bạn còn nợ ${fmtMoney(remain)}đ học phí tháng ${row.month}. Vui lòng thanh toán sớm.`,
        audience: "students", createdBy: adminId, targetUserId: student.id,
      });

    },
    onSuccess: () => toast.success("Đã gửi nhắc đóng học phí"),
  });

  if (feesQ.isLoading || profileQ.isLoading) {
    return <div className="grid place-items-center py-10"><Loader2 className="size-5 animate-spin" /></div>;
  }

  const rowsByMonth = new Map(feesQ.data?.map((f) => [f.month, f]) ?? []);

  return (
    <div className="space-y-4">
      <div className="rounded-lg border p-3 bg-muted/30">
        <Label className="text-xs">Học phí mặc định / tháng (VNĐ)</Label>
        <div className="flex gap-2 mt-1.5">
          <Input type="number" min={0} value={tuitionInput}
            onChange={(e) => setTuitionInput(e.target.value)} className="flex-1" />
          <Button size="sm" disabled={!canMutate || saveTuitionMut.isPending}
            onClick={() => saveTuitionMut.mutate()}>
            {saveTuitionMut.isPending ? <Loader2 className="size-4 animate-spin" /> : <><Save className="size-4 mr-1" />Lưu</>}
          </Button>
        </div>
      </div>

      <div className="rounded-lg border overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-muted/50 text-xs">
            <tr>
              <th className="text-left p-2 font-medium">Tháng</th>
              <th className="text-right p-2 font-medium">Phí</th>
              <th className="text-left p-2 font-medium">Đã đóng</th>
              <th className="text-left p-2 font-medium">Trạng thái</th>
              <th className="p-2"></th>
            </tr>
          </thead>
          <tbody>
            {months.map((m) => {
              const row = rowsByMonth.get(m);
              if (!row) {
                return (
                  <tr key={m} className="border-t">
                    <td className="p-2 font-medium">{m}</td>
                    <td colSpan={4} className="p-2 text-xs text-muted-foreground italic">Chưa có dữ liệu</td>
                  </tr>
                );
              }
              const total = Number(row.total_fee);
              const paid = Number(row.paid_amount);
              const status = row.is_paid || paid >= total ? "full" : paid > 0 ? "partial" : "none";
              const editing = editPaid[row.id];
              return (
                <tr key={m} className="border-t">
                  <td className="p-2 font-medium">{m}</td>
                  <td className="p-2 text-right text-muted-foreground">{fmtMoney(total)}</td>
                  <td className="p-2">
                    <Input type="number" min={0} value={editing ?? String(paid)}
                      onChange={(e) => setEditPaid((s) => ({ ...s, [row.id]: e.target.value }))}
                      onBlur={() => {
                        if (editing !== undefined && +editing !== paid) {
                          updateFeeMut.mutate({ row, paid: +editing || 0 });
                        }
                      }}
                      className="h-8 w-28" />
                  </td>
                  <td className="p-2">
                    {status === "full" ? (
                      <Badge className="bg-emerald-100 text-emerald-700 hover:bg-emerald-100">✓ Đủ</Badge>
                    ) : status === "partial" ? (
                      <Badge className="bg-amber-100 text-amber-700 hover:bg-amber-100">Một phần</Badge>
                    ) : (
                      <Badge className="bg-red-100 text-red-700 hover:bg-red-100">Chưa đóng</Badge>
                    )}
                  </td>
                  <td className="p-2 text-right whitespace-nowrap">
                    {!row.is_paid && (
                      <Button size="sm" variant="ghost" disabled={!canMutate || updateFeeMut.isPending}
                        onClick={() => updateFeeMut.mutate({ row, paid: total, markPaid: true })}
                        title="Đánh dấu đã đóng đủ">
                        <CheckCircle2 className="size-4 text-emerald-600" />
                      </Button>
                    )}
                    {status !== "full" && (
                      <Button size="sm" variant="ghost" disabled={remindMut.isPending}
                        onClick={() => remindMut.mutate(row)} title="Gửi nhắc đóng">
                        <Bell className="size-4 text-amber-600" />
                      </Button>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function TeacherSalaryTab({ teacher, adminId, centerId }: { teacher: UserRow; adminId?: string; centerId?: string | null }) {
  const qc = useQueryClient();
  const canMutate = useCanMutate();
  const months = React.useMemo(() => lastNMonths(6), []);
  const cur = curMonth();

  const profileQ = useQuery({
    queryKey: ["salary-profile", teacher.id],
    queryFn: async () => {
      const { data, error } = await supabase
        .rpc("get_profile_private", { _user_id: teacher.id });
      if (error) throw error;
      const row = (data ?? [])[0] as { monthly_salary: number | null } | undefined;
      return { monthly_salary: row?.monthly_salary ?? null };
    },
  });

  const salariesQ = useQuery({
    queryKey: ["salaries", teacher.id],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("teacher_salaries").select("*")
        .eq("teacher_id", teacher.id).in("month", months);
      if (error) throw error;
      return (data ?? []) as SalaryRow[];
    },
  });

  React.useEffect(() => {
    if (!salariesQ.data || !profileQ.data) return;
    const hasCur = salariesQ.data.some((s) => s.month === cur);
    const sal = Number(profileQ.data.monthly_salary ?? 0);
    if (!hasCur && sal > 0 && centerId) {
      supabase.from("teacher_salaries").insert({
        teacher_id: teacher.id, month: cur,
        salary_amount: sal, is_paid: false, created_by: adminId ?? null,
        center_id: centerId,
      }).then(() => qc.invalidateQueries({ queryKey: ["salaries", teacher.id] }));
    }

  }, [salariesQ.data, profileQ.data, cur, teacher.id, adminId, centerId, qc]);

  const [salaryInput, setSalaryInput] = React.useState<string>("");
  React.useEffect(() => {
    if (profileQ.data) setSalaryInput(String(profileQ.data.monthly_salary ?? 0));
  }, [profileQ.data]);

  const saveSalaryMut = useMutation({
    mutationFn: async () => {
      const v = Number(salaryInput) || 0;
      const { error } = await supabase.from("profiles")
        .update({ monthly_salary: v }).eq("id", teacher.id);
      if (error) throw error;
    },
    onSuccess: () => {
      toast.success("Đã lưu mức lương mặc định");
      qc.invalidateQueries({ queryKey: ["salary-profile", teacher.id] });
    },
    onError: (e: any) => toast.error(e?.message ?? "Lỗi"),
  });

  const markPaidMut = useMutation({
    mutationFn: async (row: SalaryRow) => {
      const { error } = await supabase.from("teacher_salaries")
        .update({ is_paid: true, paid_at: new Date().toISOString() })
        .eq("id", row.id);
      if (error) throw error;
      return row;
    },
    onSuccess: async (row) => {
      await notifyUsers({
        title: `Đã trả lương tháng ${row.month}`,
        body: `Lương tháng ${row.month} (${fmtMoney(row.salary_amount)}đ) đã được chuyển. Vui lòng kiểm tra.`,
        audience: "teachers", createdBy: adminId, targetUserId: teacher.id,
      });

      toast.success("Đã ghi nhận thanh toán & gửi thông báo");
      qc.invalidateQueries({ queryKey: ["salaries", teacher.id] });
    },
    onError: (e: any) => toast.error(e?.message ?? "Lỗi"),
  });

  if (salariesQ.isLoading || profileQ.isLoading) {
    return <div className="grid place-items-center py-10"><Loader2 className="size-5 animate-spin" /></div>;
  }

  const rowsByMonth = new Map(salariesQ.data?.map((s) => [s.month, s]) ?? []);
  const curRow = rowsByMonth.get(cur);

  return (
    <div className="space-y-4">
      <div className="rounded-lg border p-3 bg-muted/30">
        <Label className="text-xs">Lương mặc định / tháng (VNĐ)</Label>
        <div className="flex gap-2 mt-1.5">
          <Input type="number" min={0} value={salaryInput}
            onChange={(e) => setSalaryInput(e.target.value)} className="flex-1" />
          <Button size="sm" disabled={!canMutate || saveSalaryMut.isPending}
            onClick={() => saveSalaryMut.mutate()}>
            {saveSalaryMut.isPending ? <Loader2 className="size-4 animate-spin" /> : <><Save className="size-4 mr-1" />Lưu</>}
          </Button>
        </div>
      </div>

      {curRow && (
        <div className="rounded-lg border p-4 bg-gradient-to-br from-indigo-50 to-blue-50">
          <div className="flex items-center justify-between">
            <div>
              <div className="text-xs text-muted-foreground">Lương tháng {cur}</div>
              <div className="text-2xl font-bold mt-0.5">{fmtMoney(curRow.salary_amount)}đ</div>
              {curRow.is_paid ? (
                <Badge className="bg-emerald-100 text-emerald-700 hover:bg-emerald-100 mt-2">
                  ✓ Đã trả {curRow.paid_at ? format(new Date(curRow.paid_at), "dd/MM/yyyy") : ""}
                </Badge>
              ) : (
                <Badge className="bg-amber-100 text-amber-700 hover:bg-amber-100 mt-2">Chưa trả</Badge>
              )}
            </div>
            {!curRow.is_paid && (
              <Button disabled={!canMutate || markPaidMut.isPending}
                onClick={() => markPaidMut.mutate(curRow)}>
                {markPaidMut.isPending ? <Loader2 className="size-4 animate-spin" /> : <><DollarSign className="size-4 mr-1" />Ghi đã trả</>}
              </Button>
            )}
          </div>
        </div>
      )}

      <div className="rounded-lg border overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-muted/50 text-xs">
            <tr>
              <th className="text-left p-2 font-medium">Tháng</th>
              <th className="text-right p-2 font-medium">Lương</th>
              <th className="text-left p-2 font-medium">Trạng thái</th>
              <th className="p-2"></th>
            </tr>
          </thead>
          <tbody>
            {months.map((m) => {
              const row = rowsByMonth.get(m);
              if (!row) {
                return (
                  <tr key={m} className="border-t">
                    <td className="p-2 font-medium">{m}</td>
                    <td colSpan={3} className="p-2 text-xs text-muted-foreground italic">Chưa có dữ liệu</td>
                  </tr>
                );
              }
              return (
                <tr key={m} className="border-t">
                  <td className="p-2 font-medium">{m}</td>
                  <td className="p-2 text-right text-muted-foreground">{fmtMoney(row.salary_amount)}</td>
                  <td className="p-2">
                    {row.is_paid ? (
                      <Badge className="bg-emerald-100 text-emerald-700 hover:bg-emerald-100">✓ Đã trả</Badge>
                    ) : (
                      <Badge className="bg-amber-100 text-amber-700 hover:bg-amber-100">Chưa trả</Badge>
                    )}
                  </td>
                  <td className="p-2 text-right">
                    {!row.is_paid && (
                      <Button size="sm" variant="ghost" disabled={!canMutate || markPaidMut.isPending}
                        onClick={() => markPaidMut.mutate(row)}>
                        <CheckCircle2 className="size-4 text-emerald-600" />
                      </Button>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function UsersPage() {
  const qc = useQueryClient();
  const { isDemo, user } = useAuth();
  const canMutate = useCanMutate();
  const [upgradeOpen, setUpgradeOpen] = React.useState(false);
  const ent = useEntitlements();
  const [tab, setTab] = React.useState<Tab>("student");
  const [q, setQ] = React.useState("");
  const [statusFilter, setStatusFilter] = React.useState<StatusFilter>("all");

  const refreshDashboardData = React.useCallback(() => {
    qc.invalidateQueries({ queryKey: ["entitlements"] });
    qc.invalidateQueries({ queryKey: ["admin-dashboard"] });
    qc.invalidateQueries({ queryKey: ["admin-students"] });
    qc.invalidateQueries({ queryKey: ["admin-users"] });
    qc.invalidateQueries({ queryKey: ["admin", "users-by-role"] });
    qc.invalidateQueries({ queryKey: ["profiles"] });
    if (typeof window !== "undefined") {
      window.dispatchEvent(new Event("ezyclass:dashboard-refresh"));
    }
  }, [qc]);

  const usersQ = useQuery({
    queryKey: ["admin", "users-by-role", tab],
    enabled: !isDemo,
    queryFn: async () => {
      const { data: roles, error } = await supabase
        .from("user_roles").select("user_id").eq("role", tab);
      if (error) throw error;
      const ids = (roles ?? []).map((r) => r.user_id);
      if (!ids.length) return [] as UserRow[];
      const { data, error: e2 } = await supabase
        .from("profiles").select("id, display_name, email, avatar_key, avatar_url, is_banned").in("id", ids);
      if (e2) throw e2;
      const { data: privs } = await supabase.rpc("admin_list_profiles_private", { _ids: ids });
      const privMap = new Map(
        ((privs ?? []) as Array<{ id: string; phone: string | null; address: string | null; date_of_birth: string | null }>).map(
          (p) => [p.id, p],
        ),
      );
      return ((data ?? []) as Array<Record<string, unknown>>).map((u) => {
        const priv = privMap.get(u.id as string);
        return {
          ...u,
          phone: priv?.phone ?? null,
          address: priv?.address ?? null,
          date_of_birth: priv?.date_of_birth ?? null,
        };
      }) as UserRow[];
    },
  });

  const classesQ = useQuery({
    queryKey: ["admin", "classes-min"],
    enabled: !isDemo,
    queryFn: async () => {
      const { data, error } = await supabase
        .from("classes").select("id, name, teacher_id").order("name");
      if (error) throw error;
      return (data ?? []) as ClassOpt[];
    },
  });

  const enrollQ = useQuery({
    queryKey: ["admin", "enrollments-all"],
    enabled: !isDemo && tab === "student",
    queryFn: async () => {
      const { data, error } = await supabase
        .from("enrollments").select("id, class_id, student_id").eq("is_active", true);
      if (error) throw error;
      return (data ?? []) as Enrollment[];
    },
  });

  const [selected, setSelected] = React.useState<UserRow | null>(null);
  const [addClassId, setAddClassId] = React.useState("");

  const classMap = React.useMemo(() => {
    const m = new Map<string, ClassOpt>();
    (classesQ.data ?? []).forEach((c) => m.set(c.id, c));
    return m;
  }, [classesQ.data]);

  const enrollByStudent = React.useMemo(() => {
    const m = new Map<string, Enrollment[]>();
    (enrollQ.data ?? []).forEach((e) => {
      const arr = m.get(e.student_id) ?? [];
      arr.push(e);
      m.set(e.student_id, arr);
    });
    return m;
  }, [enrollQ.data]);

  const classesByTeacher = React.useMemo(() => {
    const m = new Map<string, ClassOpt[]>();
    (classesQ.data ?? []).forEach((c) => {
      if (!c.teacher_id) return;
      const arr = m.get(c.teacher_id) ?? [];
      arr.push(c);
      m.set(c.teacher_id, arr);
    });
    return m;
  }, [classesQ.data]);

  const addEnrollMut = useMutation({
    mutationFn: async (vars: { studentId: string; classId: string }) => {
      const { error } = await supabase.from("enrollments").insert({
        student_id: vars.studentId, class_id: vars.classId,
      });
      if (error) throw error;
      return vars;
    },
    onSuccess: async (vars) => {
      const className = classMap.get(vars.classId)?.name ?? "";
      await notifyUsers({
        title: "Bạn đã được ghi danh vào lớp mới",
        body: `Bạn đã được ghi danh vào lớp "${className}". Vào mục Lịch học để xem chi tiết.`,
        audience: "students", classId: vars.classId, createdBy: user?.id, targetUserId: vars.studentId,
      });

      toast.success("Đã ghi danh và gửi thông báo cho học sinh");
      qc.invalidateQueries({ queryKey: ["admin", "enrollments-all"] });
      setAddClassId("");
    },
    onError: (e: any) => toast.error(e.message ?? "Không ghi danh được"),
  });

  const removeEnrollMut = useMutation({
    mutationFn: async (vars: { id: string; classId: string }) => {
      const { error } = await supabase.from("enrollments").update({
        is_active: false,
        left_at: new Date().toISOString(),
        reason: "removed",
      }).eq("id", vars.id);
      if (error) throw error;
      return vars;
    },
    onSuccess: async (vars) => {
      const className = classMap.get(vars.classId)?.name ?? "";
      await notifyUsers({
        title: "Bạn đã bị bỏ khỏi lớp",
        body: `Bạn không còn trong lớp "${className}".`,
        audience: "students", classId: vars.classId, createdBy: user?.id,
      });
      toast.success("Đã gỡ");
      qc.invalidateQueries({ queryKey: ["admin", "enrollments-all"] });
    },
  });

  const assignTeacherMut = useMutation({
    mutationFn: async (vars: { teacherId: string; classId: string }) => {
      const { error } = await supabase
        .from("classes").update({ teacher_id: vars.teacherId }).eq("id", vars.classId);
      if (error) throw error;
      return vars;
    },
    onSuccess: async (vars) => {
      const className = classMap.get(vars.classId)?.name ?? "";
      await notifyUsers({
        title: "Bạn được phân công lớp mới",
        body: `Bạn đã được phân công phụ trách lớp "${className}". Vào mục Điểm danh để xem lịch dạy.`,
        audience: "teachers", classId: vars.classId, createdBy: user?.id, targetUserId: vars.teacherId,
      });

      toast.success("Đã phân công và gửi thông báo cho giáo viên");
      qc.invalidateQueries({ queryKey: ["admin", "classes-min"] });
      qc.invalidateQueries({ queryKey: ["admin", "classes"] });
      setAddClassId("");
    },
    onError: (e: any) => toast.error(e.message ?? "Không phân công được"),
  });

  const unassignTeacherMut = useMutation({
    mutationFn: async (classId: string) => {
      const { error } = await supabase
        .from("classes").update({ teacher_id: null }).eq("id", classId);
      if (error) throw error;
    },
    onSuccess: () => {
      toast.success("Đã gỡ");
      qc.invalidateQueries({ queryKey: ["admin", "classes-min"] });
      qc.invalidateQueries({ queryKey: ["admin", "classes"] });
    },
  });

  const filtered = (usersQ.data ?? []).filter((u) => {
    const term = q.toLowerCase().trim();
    const matchSearch = !term ||
      (u.display_name ?? "").toLowerCase().includes(term) ||
      u.email.toLowerCase().includes(term);
    if (!matchSearch) return false;
    if (statusFilter === "all") return true;
    if (statusFilter === "banned") return u.is_banned === true;
    const hasClass = tab === "student"
      ? (enrollByStudent.get(u.id)?.length ?? 0) > 0
      : (classesByTeacher.get(u.id)?.length ?? 0) > 0;
    if (statusFilter === "active") return !u.is_banned && hasClass;
    if (statusFilter === "no_class") return !u.is_banned && !hasClass;
    return true;
  });

  const selectedStudentEnrolls = selected && tab === "student"
    ? (enrollByStudent.get(selected.id) ?? []) : [];
  const selectedTeacherClasses = selected && tab === "teacher"
    ? (classesByTeacher.get(selected.id) ?? []) : [];

  const availableClassesForStudent = (classesQ.data ?? []).filter(
    (c) => !selectedStudentEnrolls.some((e) => e.class_id === c.id),
  );
  const availableClassesForTeacher = (classesQ.data ?? []).filter(
    (c) => c.teacher_id !== selected?.id,
  );

  const createAccountFn = useServerFn(createUserAccount);
  const [createOpen, setCreateOpen] = React.useState(false);
  const [newPassword, setNewPassword] = React.useState("");
  const [newName, setNewName] = React.useState("");
  const [newRole, setNewRole] = React.useState<"teacher" | "student">(tab);
  const [newPhone, setNewPhone] = React.useState("");
  const [newAddress, setNewAddress] = React.useState("");
  const [newDob, setNewDob] = React.useState("");
  const [newTuitionOrSalary, setNewTuitionOrSalary] = React.useState(0);

  React.useEffect(() => { setNewRole(tab); }, [tab]);

  const createMut = useMutation({
    mutationFn: async () => {
      const result = await createAccountFn({
        data: {
          password: newPassword,
          displayName: newName.trim(),
          role: newRole,
          phone: newPhone.trim(),
          address: newAddress.trim() || undefined,
          dateOfBirth: newDob || undefined,
        },
      });
      const newUserId = (result as any)?.id;
      if (newTuitionOrSalary > 0 && newUserId) {
        const patch = newRole === "student"
          ? { monthly_tuition: newTuitionOrSalary }
          : { monthly_salary: newTuitionOrSalary };
        await supabase.from("profiles").update(patch).eq("id", newUserId);
      }
      return result;
    },
    onSuccess: (result) => {
      const email = (result as { email?: string })?.email;
      toast.success(email ? `Đã tạo tài khoản! Email: ${email}` : "Tạo tài khoản thành công", { duration: 8000 });
      setCreateOpen(false);
      setNewPassword(""); setNewName("");
      setNewPhone(""); setNewAddress(""); setNewDob("");
      setNewTuitionOrSalary(0);
      refreshDashboardData();
    },
    onError: (e: any) => toast.error(e?.message ?? "Không tạo được tài khoản"),
  });


  const resetPwFn = useServerFn(resetUserPassword);
  const [resetPw, setResetPw] = React.useState("");
  React.useEffect(() => { setResetPw(""); }, [selected]);
  const resetPwMut = useMutation({
    mutationFn: () => resetPwFn({ data: { userId: selected!.id, newPassword: resetPw } }),
    onSuccess: async () => {
      await notifyUsers({
        title: "Mật khẩu đã được đặt lại",
        body: "Quản trị viên đã đặt lại mật khẩu cho bạn. Hãy đăng nhập bằng mật khẩu mới.",
        audience: tab === "teacher" ? "teachers" : "students",
        createdBy: user?.id, targetUserId: selected!.id,
      });

      toast.success("Đã đặt lại mật khẩu");
      setResetPw("");
    },
    onError: (e: any) => toast.error(e?.message ?? "Không đặt lại được"),
  });

  const banFn = useServerFn(banUser);
  const unbanFn = useServerFn(unbanUser);
  const banMut = useMutation({
    mutationFn: () => banFn({ data: { userId: selected!.id } }),
    onSuccess: async () => {
      await notifyUsers({
        title: "Tài khoản đã bị khóa",
        body: "Tài khoản của bạn đã bị khóa bởi quản trị viên.",
        audience: tab === "teacher" ? "teachers" : "students",
        createdBy: user?.id, targetUserId: selected!.id,
      });

      toast.success("Đã khóa tài khoản");
      refreshDashboardData();
      setSelected((s) => s ? { ...s, is_banned: true } : s);
    },
    onError: (e: any) => toast.error(e?.message ?? "Không khóa được"),
  });
  const unbanMut = useMutation({
    mutationFn: () => unbanFn({ data: { userId: selected!.id } }),
    onSuccess: async () => {
      await notifyUsers({
        title: "Tài khoản đã được mở khóa",
        body: "Tài khoản của bạn đã được mở khóa. Bạn có thể đăng nhập lại.",
        audience: tab === "teacher" ? "teachers" : "students",
        createdBy: user?.id, targetUserId: selected!.id,
      });

      toast.success("Đã mở khóa tài khoản");
      refreshDashboardData();
      setSelected((s) => s ? { ...s, is_banned: false } : s);
    },
    onError: (e: any) => toast.error(e?.message ?? "Không mở khóa được"),
  });

  const labelForTab = tab === "student" ? "học sinh" : "giáo viên";

  function StatusBadge({ u }: { u: UserRow }) {
    if (u.is_banned) return <Badge variant="destructive">🔒 Đã khóa</Badge>;
    const hasClass = tab === "student"
      ? (enrollByStudent.get(u.id)?.length ?? 0) > 0
      : (classesByTeacher.get(u.id)?.length ?? 0) > 0;
    if (hasClass) {
      return <Badge className="bg-emerald-100 text-emerald-700 hover:bg-emerald-100">● {tab === "student" ? "Đang học" : "Đang dạy"}</Badge>;
    }
    return <Badge className="bg-amber-100 text-amber-700 hover:bg-amber-100">● Không có lớp</Badge>;
  }

  function RowAvatar({ u }: { u: UserRow }) {
    if (u.avatar_url) {
      return <img src={u.avatar_url} alt="" className="size-8 rounded-full object-cover shrink-0" />;
    }
    return <UserAvatar avatarKey={u.avatar_key} fallback={u.display_name ?? u.email} size="sm" />;
  }

  return (
    <PortalLayout
      portal="admin" title="Quản lý người dùng"
      breadcrumb={[{ label: "Admin", to: "/admin" }, { label: "Người dùng" }]}
    >
      <Tabs value={tab} onValueChange={(v) => { setTab(v as Tab); setSelected(null); }} className="mb-4">
        <TabsList>
          <TabsTrigger value="student">Học sinh</TabsTrigger>
          {ent.maxTeachers !== 0 && <TabsTrigger value="teacher">Giáo viên</TabsTrigger>}
        </TabsList>
      </Tabs>
      {ent.maxTeachers === 0 && (
        <div className="mb-4 text-sm text-muted-foreground bg-muted/50 p-3 rounded-lg">
          Gói {ent.tier === "free" ? "Miễn phí" : "Gia sư"} không hỗ trợ tạo giáo viên. Admin sẽ được tự động gán làm giáo viên.
          {" "}<Link to="/billing" className="text-blue-600 hover:underline">Nâng cấp</Link>
        </div>
      )}

      <div className="flex items-center gap-3 mb-6 flex-wrap">
        <div className="relative flex-1 max-w-md min-w-[220px]">
          <Search className="size-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
          <Input
            value={q} onChange={(e) => setQ(e.target.value)}
            placeholder={`Tìm ${labelForTab} theo tên hoặc email`} className="pl-9"
          />
        </div>
        <Select value={statusFilter} onValueChange={(v) => setStatusFilter(v as StatusFilter)}>
          <SelectTrigger className="w-44"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Tất cả</SelectItem>
            <SelectItem value="active">● Đang hoạt động</SelectItem>
            <SelectItem value="no_class">● Không có lớp</SelectItem>
            <SelectItem value="banned">🔒 Đã khóa</SelectItem>
          </SelectContent>
        </Select>
        <span className="text-sm text-muted-foreground">
          {filtered.length} {labelForTab}
        </span>
        <Button
          variant="outline"
          size="sm"
          className="ml-auto gap-1.5 dark:border-slate-700"
          disabled={filtered.length === 0}
          title={ent.exportExcel ? "Xuất danh sách ra CSV" : "Nâng cấp gói Trung tâm để xuất dữ liệu"}
          onClick={() => {
            if (!ent.exportExcel) { setUpgradeOpen(true); return; }
            if (tab === "student") {
              const columns = [
                { key: "name", label: "Tên" },
                { key: "email", label: "Email" },
                { key: "phone", label: "SĐT" },
                { key: "classes", label: "Lớp đang học" },
                { key: "status", label: "Trạng thái" },
              ];
              const rows = filtered.map((u) => {
                const enrolls = enrollByStudent.get(u.id) ?? [];
                const classNames = enrolls
                  .map((e) => classMap.get(e.class_id)?.name)
                  .filter(Boolean)
                  .join("; ");
                return {
                  name: u.display_name ?? "",
                  email: u.email,
                  phone: u.phone ?? "",
                  classes: classNames,
                  status: u.is_banned ? "Tạm khoá" : "Hoạt động",
                };
              });
              exportToCSV(`HocSinh_ToanTrungTam_${todayStamp()}`, columns, rows);
              toast.success(`Đã xuất ${rows.length} dòng`);
            } else {
              const columns = [
                { key: "name", label: "Tên" },
                { key: "email", label: "Email" },
                { key: "phone", label: "SĐT" },
                { key: "classCount", label: "Số lớp dạy" },
                { key: "status", label: "Trạng thái" },
              ];
              const rows = filtered.map((u) => ({
                name: u.display_name ?? "",
                email: u.email,
                phone: u.phone ?? "",
                classCount: (classesByTeacher.get(u.id) ?? []).length,
                status: u.is_banned ? "Tạm khoá" : "Hoạt động",
              }));
              exportToCSV(`GiaoVien_${todayStamp()}`, columns, rows);
              toast.success(`Đã xuất ${rows.length} dòng`);
            }
          }}
        >
          <Download className="size-4" /> Xuất CSV
        </Button>
        <Dialog open={createOpen} onOpenChange={setCreateOpen}>
          <Button
            disabled={!canMutate}
            onClick={() => {
              const studentCount = (usersQ.data ?? []).filter((u) => !u.is_banned).length;
              const maxStudents = ent.maxStudents === -1 ? Infinity : ent.maxStudents;
              if (tab === "student" && studentCount >= maxStudents) {
                setUpgradeOpen(true);
              } else {
                setCreateOpen(true);
              }
            }}
          >
            <UserPlus className="size-4 mr-1" /> Tạo tài khoản
          </Button>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Tạo tài khoản mới</DialogTitle>
            </DialogHeader>
            <div className="space-y-3">
              <div>
                <Label>Vai trò</Label>
                <Select value={newRole} onValueChange={(v) => setNewRole(v as any)}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="student">Học sinh</SelectItem>
                    {ent.maxTeachers !== 0 && <SelectItem value="teacher">Giáo viên</SelectItem>}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Họ và tên</Label>
                <Input value={newName} onChange={(e) => setNewName(e.target.value)} placeholder="Nguyễn Văn A" />
              </div>
              <div>
                <Label>Số điện thoại *</Label>
                <Input type="tel" value={newPhone} onChange={(e) => setNewPhone(e.target.value)} placeholder="0901234567" />
              </div>
              <div>
                <Label>Địa chỉ</Label>
                <Input value={newAddress} onChange={(e) => setNewAddress(e.target.value)} placeholder="123 Nguyễn Huệ, Q.1, TP.HCM" />
              </div>
              <div>
                <Label>Ngày sinh</Label>
                <Input type="date" value={newDob} onChange={(e) => setNewDob(e.target.value)} />
              </div>
              <div>
                <Label>{newRole === "student" ? "Học phí / tháng (VNĐ)" : "Lương / tháng (VNĐ)"}</Label>
                <Input type="number" min={0} value={newTuitionOrSalary}
                  onChange={(e) => setNewTuitionOrSalary(+e.target.value || 0)}
                  placeholder={newRole === "student" ? "500000" : "5000000"} />
              </div>
              <div>
                <Label>Email đăng nhập</Label>
                <div className="rounded-lg bg-muted/50 border border-dashed p-3 text-sm text-muted-foreground">
                  <span className="font-medium text-foreground">Tự động tạo</span> khi hoàn tất
                  <br />
                  <span className="text-xs">VD: 000042@{newRole}.ezyclass</span>
                </div>
              </div>
              <div>
                <Label>Mật khẩu tạm</Label>
                <Input type="text" value={newPassword} onChange={(e) => setNewPassword(e.target.value)} placeholder="Tối thiểu 6 ký tự" />
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setCreateOpen(false)}>Hủy</Button>
              <Button
                disabled={createMut.isPending || newPassword.length < 6 || !newName || !newPhone || newPhone.length < 6}
                onClick={() => createMut.mutate()}
              >

                {createMut.isPending ? <Loader2 className="size-4 animate-spin" /> : "Tạo tài khoản"}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>

      {isDemo && (
        <div className="mb-4 rounded-lg border bg-muted/40 p-3 text-sm text-muted-foreground">
          Bạn đang ở chế độ demo. Đăng nhập tài khoản admin thật để quản lý người dùng.
        </div>
      )}

      {usersQ.isLoading ? (
        <div className="grid place-items-center py-20"><Loader2 className="size-6 animate-spin text-muted-foreground" /></div>
      ) : (
        <div className="rounded-2xl border bg-card overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-muted/50 text-xs uppercase text-muted-foreground">
              <tr>
                <th className="text-left p-3 font-medium">{tab === "student" ? "Học sinh" : "Giáo viên"}</th>
                <th className="text-left p-3 font-medium">Email</th>
                <th className="text-left p-3 font-medium">Trạng thái</th>
                <th className="text-left p-3 font-medium">{tab === "student" ? "Lớp đang học" : "Lớp đang dạy"}</th>
                <th className="p-3"></th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((u) => {
                const items = tab === "student"
                  ? (enrollByStudent.get(u.id) ?? []).map((e) => classMap.get(e.class_id)?.name ?? "?")
                  : (classesByTeacher.get(u.id) ?? []).map((c) => c.name);
                return (
                  <tr key={u.id} className={`border-t hover:bg-muted/30 transition-colors ${u.is_banned ? "opacity-60" : ""}`}>
                    <td className="p-3">
                      <div className="flex items-center gap-2.5">
                        <RowAvatar u={u} />
                        <span className="font-medium">{u.display_name ?? u.email.split("@")[0]}</span>
                      </div>
                    </td>
                    <td className="p-3 text-muted-foreground">{u.email}</td>
                    <td className="p-3"><StatusBadge u={u} /></td>
                    <td className="p-3">
                      <div className="flex flex-wrap gap-1">
                        {items.length === 0
                          ? <span className="text-xs text-muted-foreground">—</span>
                          : items.map((name, i) => (
                              <Badge key={i} variant="secondary" className="text-[10px]">{name}</Badge>
                            ))}
                      </div>
                    </td>
                    <td className="p-3 text-right">
                      <Button size="sm" variant="outline"
                        onClick={() => setSelected(u)}>Quản lý</Button>
                    </td>
                  </tr>
                );
              })}
              {filtered.length === 0 && (
                <tr><td colSpan={5} className="p-10 text-center text-muted-foreground">Không có {labelForTab} nào.</td></tr>
              )}
            </tbody>
          </table>
        </div>
      )}

      <Dialog open={!!selected} onOpenChange={(o) => !o && setSelected(null)}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Quản lý tài khoản</DialogTitle>
          </DialogHeader>
          {selected && (
            <div className="flex items-center gap-4 pb-4 border-b">
              <div className="shrink-0">
                {selected.avatar_url ? (
                  <img src={selected.avatar_url} alt="" className="size-16 rounded-full object-cover ring-2" style={{ ["--tw-ring-color" as never]: "var(--portal-accent)" }} />
                ) : (
                  <UserAvatar avatarKey={selected.avatar_key} fallback={selected.display_name ?? selected.email} size="lg" />
                )}
              </div>
              <div className="min-w-0 flex-1">
                <div className="font-display text-lg font-semibold truncate">{selected.display_name ?? "—"}</div>
                <div className="text-sm text-muted-foreground truncate">{selected.email}</div>
                <div className="mt-1"><StatusBadge u={selected} /></div>
              </div>
            </div>
          )}
          {selected && (
            <Tabs defaultValue="classes" key={selected.id} className="mt-3">
              <TabsList className={ent.finance ? "grid grid-cols-4 w-full" : "grid grid-cols-3 w-full"}>
                <TabsTrigger value="classes">{tab === "student" ? "Lớp học" : "Lớp dạy"}</TabsTrigger>
                {ent.finance && <TabsTrigger value="finance">{tab === "student" ? "Học phí" : "Lương"}</TabsTrigger>}
                <TabsTrigger value="history">Lịch sử</TabsTrigger>
                <TabsTrigger value="info">Thông tin</TabsTrigger>
              </TabsList>

              <TabsContent value="history" className="mt-4">
                {tab === "student"
                  ? <StudentHistoryTab studentId={selected.id} />
                  : <TeacherHistoryTab teacherId={selected.id} />}
              </TabsContent>

              <TabsContent value="classes" className="mt-4">
                {tab === "student" ? (
                  <div className="space-y-4">
                    <div>
                      <Label className="mb-2 block">Lớp đang theo học</Label>
                      {selectedStudentEnrolls.length === 0 ? (
                        <p className="text-sm text-muted-foreground">Chưa ghi danh lớp nào.</p>
                      ) : (
                        <div className="space-y-1.5">
                          {selectedStudentEnrolls.map((e) => (
                            <div key={e.id} className="flex items-center justify-between p-2 rounded-lg border">
                              <span className="text-sm">{classMap.get(e.class_id)?.name ?? "?"}</span>
                              <Button size="icon" variant="ghost"
                                disabled={!canMutate || removeEnrollMut.isPending}
                                onClick={() => removeEnrollMut.mutate({ id: e.id, classId: e.class_id })}>
                                <X className="size-4" />
                              </Button>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                    <div className="pt-3 border-t">
                      <Label className="mb-2 block">Ghi danh lớp mới</Label>
                      <div className="flex gap-2">
                        <Select value={addClassId} onValueChange={setAddClassId}>
                          <SelectTrigger className="flex-1"><SelectValue placeholder="Chọn lớp" /></SelectTrigger>
                          <SelectContent>
                            {availableClassesForStudent.map((c) => (
                              <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                        <Button
                          disabled={!canMutate || !addClassId || addEnrollMut.isPending}
                          onClick={() => addEnrollMut.mutate({ studentId: selected.id, classId: addClassId })}
                        >
                          <UserPlus className="size-4 mr-1" /> Thêm
                        </Button>
                      </div>
                    </div>
                  </div>
                ) : (
                  <div className="space-y-4">
                    <div>
                      <Label className="mb-2 block">Lớp đang phụ trách</Label>
                      {selectedTeacherClasses.length === 0 ? (
                        <p className="text-sm text-muted-foreground">Chưa được phân công lớp nào.</p>
                      ) : (
                        <div className="space-y-1.5">
                          {selectedTeacherClasses.map((c) => (
                            <div key={c.id} className="flex items-center justify-between p-2 rounded-lg border">
                              <span className="text-sm">{c.name}</span>
                              <Button size="icon" variant="ghost"
                                disabled={!canMutate || unassignTeacherMut.isPending}
                                onClick={() => unassignTeacherMut.mutate(c.id)}>
                                <X className="size-4" />
                              </Button>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                    <div className="pt-3 border-t">
                      <Label className="mb-2 block">Phân công lớp mới</Label>
                      <div className="flex gap-2">
                        <Select value={addClassId} onValueChange={setAddClassId}>
                          <SelectTrigger className="flex-1"><SelectValue placeholder="Chọn lớp" /></SelectTrigger>
                          <SelectContent>
                            {availableClassesForTeacher.map((c) => (
                              <SelectItem key={c.id} value={c.id}>
                                {c.name}{c.teacher_id ? " (đang có GV)" : ""}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                        <Button
                          disabled={!canMutate || !addClassId || assignTeacherMut.isPending}
                          onClick={() => assignTeacherMut.mutate({ teacherId: selected.id, classId: addClassId })}
                        >
                          <UserPlus className="size-4 mr-1" /> Gán
                        </Button>
                      </div>
                    </div>
                  </div>
                )}
              </TabsContent>

              <TabsContent value="finance" className="mt-4">
                {tab === "student"
                  ? <StudentFeesTab student={selected} adminId={user?.id} centerId={user?.centerId} />
                  : <TeacherSalaryTab teacher={selected} adminId={user?.id} centerId={user?.centerId} />}
              </TabsContent>

              <TabsContent value="info" className="mt-4 space-y-4">
                <div className="grid grid-cols-2 gap-3">
                  <div className="rounded-lg bg-muted/40 p-3">
                    <div className="text-[10px] uppercase tracking-wide text-muted-foreground mb-0.5">Số điện thoại</div>
                    <div className="text-sm font-medium">{selected.phone ?? "—"}</div>
                  </div>
                  <div className="rounded-lg bg-muted/40 p-3">
                    <div className="text-[10px] uppercase tracking-wide text-muted-foreground mb-0.5">Ngày sinh</div>
                    <div className="text-sm font-medium">
                      {selected.date_of_birth ? new Date(selected.date_of_birth).toLocaleDateString("vi-VN") : "—"}
                    </div>
                  </div>
                  <div className="rounded-lg bg-muted/40 p-3 col-span-2">
                    <div className="text-[10px] uppercase tracking-wide text-muted-foreground mb-0.5">Địa chỉ</div>
                    <div className="text-sm font-medium">{selected.address ?? "—"}</div>
                  </div>
                </div>

                <div className="pt-3 border-t">
                  <Label className="mb-2 block text-amber-700">🔑 Đặt lại mật khẩu</Label>
                  <div className="flex gap-2">
                    <Input
                      type="text"
                      value={resetPw}
                      onChange={(e) => setResetPw(e.target.value)}
                      placeholder="Mật khẩu mới (tối thiểu 6 ký tự)"
                      className="flex-1"
                    />
                    <Button
                      variant="outline"
                      disabled={resetPw.length < 6 || resetPwMut.isPending || !canMutate}
                      onClick={() => {
                        if (!confirm(`Đặt lại mật khẩu cho ${selected?.display_name ?? selected?.email}?`)) return;
                        resetPwMut.mutate();
                      }}
                    >
                      {resetPwMut.isPending ? <Loader2 className="size-4 animate-spin" /> : "Đặt lại"}
                    </Button>
                  </div>
                  <p className="text-xs text-muted-foreground mt-1">Mật khẩu mới sẽ có hiệu lực ngay lập tức.</p>
                </div>

                <div className="pt-3 border-t">
                  {selected.is_banned ? (
                    <div className="rounded-lg bg-red-50 border border-red-200 p-3">
                      <div className="flex items-center gap-2 mb-2">
                        <Lock className="size-4 text-red-600" />
                        <span className="text-sm font-medium text-red-700">Tài khoản đang bị khóa</span>
                      </div>
                      <Button
                        variant="outline"
                        className="border-emerald-300 text-emerald-700 hover:bg-emerald-50 hover:text-emerald-800"
                        disabled={!canMutate || unbanMut.isPending}
                        onClick={() => {
                          if (!confirm(`Mở khóa ${selected?.display_name ?? selected?.email}?`)) return;
                          unbanMut.mutate();
                        }}
                      >
                        {unbanMut.isPending ? <Loader2 className="size-4 animate-spin" /> : <><Unlock className="size-4 mr-1" /> 🔓 Mở khóa</>}
                      </Button>
                    </div>
                  ) : (
                    <div className="rounded-lg bg-muted/40 p-3">
                      <div className="flex items-center gap-2 mb-2">
                        <span className="size-2 rounded-full bg-emerald-500" />
                        <span className="text-sm font-medium">Tài khoản đang hoạt động</span>
                      </div>
                      <Button
                        variant="outline"
                        className="border-red-300 text-red-600 hover:bg-red-50 hover:text-red-700"
                        disabled={!canMutate || banMut.isPending}
                        onClick={() => {
                          if (!confirm(`Khóa tài khoản ${selected?.display_name ?? selected?.email}? Người dùng sẽ bị đăng xuất ngay.`)) return;
                          banMut.mutate();
                        }}
                      >
                        {banMut.isPending ? <Loader2 className="size-4 animate-spin" /> : <><Lock className="size-4 mr-1" /> 🔒 Khóa tài khoản</>}
                      </Button>
                    </div>
                  )}
                </div>
              </TabsContent>
            </Tabs>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setSelected(null)}>Đóng</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      <UpgradeDialog
        open={upgradeOpen}
        onOpenChange={setUpgradeOpen}
        currentTier={ent.tier}
        resource="students"
        currentCount={(usersQ.data ?? []).filter((u) => !u.is_banned).length}
        limit={ent.maxStudents === -1 ? Infinity : ent.maxStudents}
      />
    </PortalLayout>
  );
}

/* ============================================================
   STUDENT HISTORY TAB
   ============================================================ */
type EnrollHistory = {
  id: string; class_id: string; is_active: boolean;
  left_at: string | null; reason: string | null; enrolled_at: string;
};
type ClassMini = { id: string; name: string; subject: string | null; class_code: string | null; status: string; teacher_id: string | null };
type TeacherMini = { id: string; display_name: string | null };

const ENROLL_REASON: Record<string, { label: string; className: string }> = {
  active:      { label: "● Đang học",    className: "bg-emerald-100 text-emerald-700" },
  completed:   { label: "Hoàn thành", className: "bg-blue-100 text-blue-700" },
  transferred: { label: "↔ Chuyển lớp",  className: "bg-amber-100 text-amber-700" },
  dropped:     { label: "✕ Rời lớp",     className: "bg-red-100 text-red-700" },
  removed:     { label: "✕ Bị bỏ",       className: "bg-slate-100 text-slate-500" },
};

function StudentHistoryTab({ studentId }: { studentId: string }) {
  const [search, setSearch] = React.useState("");
  const historyQ = useQuery({
    queryKey: ["admin", "student-history", studentId],
    queryFn: async () => {
      const { data: enrolls } = await supabase
        .from("enrollments")
        .select("id, class_id, is_active, left_at, reason, enrolled_at")
        .eq("student_id", studentId)
        .order("enrolled_at", { ascending: false });
      const classIds = [...new Set((enrolls ?? []).map((e: any) => e.class_id))];
      if (!classIds.length) return { enrolls: [] as EnrollHistory[], classes: [] as ClassMini[], teachers: [] as TeacherMini[] };
      const { data: classes } = await supabase
        .from("classes").select("id, name, subject, class_code, status, teacher_id").in("id", classIds);
      const teacherIds = [...new Set((classes ?? []).map((c: any) => c.teacher_id).filter(Boolean))] as string[];
      const { data: teachers } = teacherIds.length
        ? await supabase.from("profiles").select("id, display_name").in("id", teacherIds)
        : { data: [] as TeacherMini[] };
      return { enrolls: (enrolls ?? []) as EnrollHistory[], classes: (classes ?? []) as ClassMini[], teachers: (teachers ?? []) as TeacherMini[] };
    },
  });

  const classMap = new Map((historyQ.data?.classes ?? []).map((c) => [c.id, c]));
  const teacherMap = new Map((historyQ.data?.teachers ?? []).map((t) => [t.id, t]));

  const enrolls = (historyQ.data?.enrolls ?? []).filter((e) => {
    if (!search.trim()) return true;
    const cls = classMap.get(e.class_id);
    if (!cls) return false;
    const term = search.toLowerCase();
    return cls.name.toLowerCase().includes(term) || (cls.class_code ?? "").toLowerCase().includes(term);
  });
  const activeCount = enrolls.filter((e) => e.is_active).length;
  const completedCount = enrolls.filter((e) => e.reason === "completed").length;

  return (
    <div className="space-y-4 mt-3">
      <div className="grid grid-cols-3 gap-2">
        <div className="rounded-lg bg-muted/40 p-2.5 text-center">
          <div className="text-lg font-semibold">{enrolls.length}</div>
          <div className="text-[10px] text-muted-foreground">Tổng lớp</div>
        </div>
        <div className="rounded-lg bg-emerald-50 p-2.5 text-center">
          <div className="text-lg font-semibold text-emerald-700">{activeCount}</div>
          <div className="text-[10px] text-muted-foreground">Đang học</div>
        </div>
        <div className="rounded-lg bg-blue-50 p-2.5 text-center">
          <div className="text-lg font-semibold text-blue-700">{completedCount}</div>
          <div className="text-[10px] text-muted-foreground">Hoàn thành</div>
        </div>
      </div>
      <Input placeholder="Tìm theo tên lớp hoặc mã lớp..." value={search} onChange={(e) => setSearch(e.target.value)} />
      {historyQ.isLoading ? (
        <div className="text-sm text-center text-muted-foreground py-4">Đang tải...</div>
      ) : enrolls.length === 0 ? (
        <div className="text-sm text-center text-muted-foreground py-4">Chưa có lịch sử lớp học.</div>
      ) : (
        <div className="space-y-2 max-h-72 overflow-y-auto pr-1">
          {enrolls.map((e) => {
            const cls = classMap.get(e.class_id);
            const teacher = cls?.teacher_id ? teacherMap.get(cls.teacher_id) : null;
            const status = e.is_active ? "active" : (e.reason ?? "removed");
            const cfg = ENROLL_REASON[status] ?? ENROLL_REASON.removed;
            return (
              <div key={e.id} className={`rounded-lg border p-3 ${e.is_active ? "" : "opacity-75"}`}>
                <div className="flex items-center justify-between mb-1.5">
                  <div className="flex items-center gap-2">
                    <code className="text-[10px] px-1.5 py-0.5 bg-muted rounded font-mono">{cls?.class_code ?? "—"}</code>
                    <span className="text-sm font-medium">{cls?.name ?? "—"}</span>
                  </div>
                  <Badge className={`text-[10px] ${cfg.className}`}>{cfg.label}</Badge>
                </div>
                <div className="text-xs text-muted-foreground flex flex-wrap gap-x-3 gap-y-1">
                  {cls?.subject && <span>{cls.subject}</span>}
                  {teacher && <span>👩‍🏫 {teacher.display_name}</span>}
                  <span>📅 {new Date(e.enrolled_at).toLocaleDateString("vi-VN")}</span>
                  {e.left_at && <span>→ {new Date(e.left_at).toLocaleDateString("vi-VN")}</span>}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

/* ============================================================
   TEACHER HISTORY TAB
   ============================================================ */
const TEACHER_REASON: Record<string, { label: string; className: string }> = {
  active:          { label: "● Đang dạy",       className: "bg-emerald-100 text-emerald-700" },
  class_completed: { label: "Lớp kết thúc",  className: "bg-blue-100 text-blue-700" },
  reassigned:      { label: "↔ Đã chuyển GV",   className: "bg-amber-100 text-amber-700" },
  resigned:        { label: "✕ Nghỉ dạy",       className: "bg-red-100 text-red-700" },
};

function TeacherHistoryTab({ teacherId }: { teacherId: string }) {
  const [search, setSearch] = React.useState("");
  const historyQ = useQuery({
    queryKey: ["admin", "teacher-history", teacherId],
    queryFn: async () => {
      const { data: records } = await supabase
        .from("teacher_class_history")
        .select("id, class_id, assigned_at, removed_at, reason")
        .eq("teacher_id", teacherId)
        .order("assigned_at", { ascending: false });
      const classIds = [...new Set((records ?? []).map((r: any) => r.class_id))];
      if (!classIds.length) return { records: [] as any[], classes: [] as ClassMini[], countMap: new Map<string, number>() };
      const { data: classes } = await supabase
        .from("classes").select("id, name, subject, class_code, status, teacher_id").in("id", classIds);
      const { data: enrollCounts } = await supabase
        .from("enrollments").select("class_id").in("class_id", classIds).eq("is_active", true);
      const countMap = new Map<string, number>();
      (enrollCounts ?? []).forEach((e: any) => countMap.set(e.class_id, (countMap.get(e.class_id) ?? 0) + 1));
      return { records: records ?? [], classes: (classes ?? []) as ClassMini[], countMap };
    },
  });

  const classMap = new Map((historyQ.data?.classes ?? []).map((c) => [c.id, c]));
  const countMap = historyQ.data?.countMap ?? new Map<string, number>();

  const records = (historyQ.data?.records ?? []).filter((r: any) => {
    if (!search.trim()) return true;
    const cls = classMap.get(r.class_id);
    if (!cls) return false;
    const term = search.toLowerCase();
    return cls.name.toLowerCase().includes(term) || (cls.class_code ?? "").toLowerCase().includes(term);
  });
  const activeCount = records.filter((r: any) => !r.removed_at).length;
  const completedCount = records.filter((r: any) => r.reason === "class_completed").length;

  return (
    <div className="space-y-4 mt-3">
      <div className="grid grid-cols-3 gap-2">
        <div className="rounded-lg bg-muted/40 p-2.5 text-center">
          <div className="text-lg font-semibold">{records.length}</div>
          <div className="text-[10px] text-muted-foreground">Tổng lớp</div>
        </div>
        <div className="rounded-lg bg-emerald-50 p-2.5 text-center">
          <div className="text-lg font-semibold text-emerald-700">{activeCount}</div>
          <div className="text-[10px] text-muted-foreground">Đang dạy</div>
        </div>
        <div className="rounded-lg bg-blue-50 p-2.5 text-center">
          <div className="text-lg font-semibold text-blue-700">{completedCount}</div>
          <div className="text-[10px] text-muted-foreground">Hoàn thành</div>
        </div>
      </div>
      <Input placeholder="Tìm theo tên lớp hoặc mã lớp..." value={search} onChange={(e) => setSearch(e.target.value)} />
      {historyQ.isLoading ? (
        <div className="text-sm text-center text-muted-foreground py-4">Đang tải...</div>
      ) : records.length === 0 ? (
        <div className="text-sm text-center text-muted-foreground py-4">Chưa có lịch sử.</div>
      ) : (
        <div className="space-y-2 max-h-72 overflow-y-auto pr-1">
          {records.map((r: any) => {
            const cls = classMap.get(r.class_id);
            const status = !r.removed_at ? "active" : (r.reason ?? "reassigned");
            const cfg = TEACHER_REASON[status] ?? TEACHER_REASON.reassigned;
            const studentCount = countMap.get(r.class_id) ?? 0;
            return (
              <div key={r.id} className={`rounded-lg border p-3 ${r.removed_at ? "opacity-75" : ""}`}>
                <div className="flex items-center justify-between mb-1.5">
                  <div className="flex items-center gap-2">
                    <code className="text-[10px] px-1.5 py-0.5 bg-muted rounded font-mono">{cls?.class_code ?? "—"}</code>
                    <span className="text-sm font-medium">{cls?.name ?? "—"}</span>
                  </div>
                  <Badge className={`text-[10px] ${cfg.className}`}>{cfg.label}</Badge>
                </div>
                <div className="text-xs text-muted-foreground flex flex-wrap gap-x-3 gap-y-1">
                  {cls?.subject && <span>{cls.subject}</span>}
                  <span>👥 {studentCount} học sinh</span>
                  <span>📅 {new Date(r.assigned_at).toLocaleDateString("vi-VN")}</span>
                  {r.removed_at && <span>→ {new Date(r.removed_at).toLocaleDateString("vi-VN")}</span>}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
